import { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@sal/authentication.react";

interface PermissionRouteProps {
	/** SALCore security context name, e.g. `Routes`. Case-sensitive. */
	securityContext: string;
	/** Permission within that context, e.g. `Home`. Case-sensitive. */
	securityContextPermission: string;
	children: ReactElement;
}

/**
 * A permission gate that sends a signed-in user with no permission to `/NoAccess` instead of
 * `/NotFound`.
 *
 * `@sal/portal`'s `AuthenticatedRoute` does two jobs: it requires a session, and — when given a
 * `securityContext`/`securityContextPermission` pair — it requires a permission, redirecting to
 * `/NotFound` if the user lacks it. That destination is right for a mistyped URL and wrong for the
 * case Admin2 now creates deliberately: sign-in auto-provisions unknown tenant users with **zero**
 * user groups, so a legitimate, correctly-signed-in person can hold no permissions at all and would
 * be told the page does not exist.
 *
 * So the two jobs are split. `AuthenticatedRoute` (bare, no permission arguments) still guards the
 * session and still owns the redirect to `/Login`; this component owns only the permission check.
 * It is deliberately tiny and additive — deleting it and putting the arguments back on
 * `AuthenticatedRoute` restores the previous behaviour exactly.
 */
export default function PermissionRoute({
	securityContext,
	securityContextPermission,
	children
}: PermissionRouteProps) {
	const auth = useAuth();

	if (!auth.userHasPermission(securityContext, securityContextPermission)) {
		return <Navigate to="/NoAccess" replace />;
	}

	return children;
}
